import React from "react";
import styled from "styled-components";
import MainContainer from "../../components/MainContainer";
import { useTheme } from "../../contexts/ThemeContext";

const PrivacyPage = () => {
  const { theme } = useTheme();

  return (
    <MainContainer>
      <PrivacyContainer $theme={theme}>
        <Header>
          <Title>PRIVACY POLICY</Title>
          <LastUpdated>Last updated: 14 July 2025</LastUpdated>
        </Header>

        <Content>
          <Introduction $theme={theme}>
            Playbun.com ("we", "us", "our") respects your privacy and is
            committed to protecting your personal information. This Privacy
            Policy explains what data we collect, how we use it, how we share it
            and your rights.
          </Introduction>

          <Section>
            <SectionTitle $theme={theme}>
              1. Information We Collect
            </SectionTitle>
            <Table $theme={theme}>
              <TableHeader $theme={theme}>
                <TableHeaderCell $theme={theme}>Category</TableHeaderCell>
                <TableHeaderCell $theme={theme}>Examples</TableHeaderCell>
              </TableHeader>
              <TableRow $theme={theme}>
                <TableCell $theme={theme}>
                  <strong>Information you provide</strong>
                </TableCell>
                <TableCell $theme={theme}>
                  Account credentials, email address, uploaded images, payment
                  details (handled by third-party processors)
                </TableCell>
              </TableRow>
              <TableRow $theme={theme}>
                <TableCell $theme={theme}>
                  <strong>Content data</strong>
                </TableCell>
                <TableCell $theme={theme}>
                  User Content (images) and AI-Generated Content
                </TableCell>
              </TableRow>
              <TableRow $theme={theme}>
                <TableCell $theme={theme}>
                  <strong>Automatically collected data</strong>
                </TableCell>
                <TableCell $theme={theme}>
                  IP address, browser type, device identifiers, log files, usage
                  statistics, cookies
                </TableCell>
              </TableRow>
              <TableRow $theme={theme}>
                <TableCell $theme={theme}>
                  <strong>Derived data</strong>
                </TableCell>
                <TableCell $theme={theme}>
                  Embeddings, model outputs, analytics
                </TableCell>
              </TableRow>
            </Table>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>
              2. How We Use Information
            </SectionTitle>
            <SectionContent>
              <BulletPoint>
                Provide & operate the Service and generate AI-Generated Content.
              </BulletPoint>
              <BulletPoint>
                Process payments and manage subscriptions.
              </BulletPoint>
              <BulletPoint>
                Improve, test and develop our models, algorithms and features.
              </BulletPoint>
              <BulletPoint>
                Monitor and secure the Service, prevent fraud and abuse.
              </BulletPoint>
              <BulletPoint>
                Comply with legal obligations and enforce our Terms.
              </BulletPoint>
              <BulletPoint>
                Communicate with you about updates, security alerts and support.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>
              3. Sharing of Information
            </SectionTitle>
            <SectionContent>
              We only share personal information as follows:
              <BulletList>
                <BulletPoint>
                  <strong>Service Providers</strong> – cloud hosting, payment
                  processors, analytics vendors and content-delivery networks,
                  bound by confidentiality agreements.
                </BulletPoint>
                <BulletPoint>
                  <strong>Legal & Compliance</strong> – when required by law,
                  subpoena or court order, or to protect rights, safety or
                  property.
                </BulletPoint>
                <BulletPoint>
                  <strong>Business Transfers</strong> – in connection with a
                  merger, acquisition or asset sale (with notice to you).
                </BulletPoint>
                <BulletPoint>
                  <strong>With Your Consent</strong> – where you explicitly
                  authorise us to share data.
                </BulletPoint>
              </BulletList>
              <ImportantNote $theme={theme}>
                <strong>Important:</strong> We never share your uploaded images
                or AI-generated videos with any third parties. Your content
                remains private and is only processed by our systems to provide
                the Service.
              </ImportantNote>
              <HighlightText $theme={theme}>
                We never sell your personal data.
              </HighlightText>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>4. Cookies & Tracking</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We use cookies and similar technologies to remember preferences,
                perform analytics, and improve the Service.
              </BulletPoint>
              <BulletPoint>
                You can disable cookies in your browser, but some features may
                not function.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>5. Data Retention</SectionTitle>
            <SectionContent>
              We retain personal data only as long as necessary for the purposes
              outlined here, unless a longer retention period is required to
              comply with law, resolve disputes or enforce our agreements.
              <SubSection>
                <SubSectionTitle $theme={theme}>
                  Content Retention:
                </SubSectionTitle>
                <p>
                  We store your uploaded images and generated videos to provide
                  you with access to your content. Content is only deleted when
                  you explicitly take a deletion action (such as deleting
                  specific content or your entire account). We do not
                  automatically delete your content - it remains available until
                  you choose to remove it.
                </p>
              </SubSection>
              <SubSection>
                <SubSectionTitle $theme={theme}>
                  Content Deletion:
                </SubSectionTitle>
                <p>
                  When you delete your content (individual images/videos or
                  through account deletion), the deletion is irreversible and we
                  immediately and permanently remove it from our systems. We do
                  not retain any copies of your deleted content.
                </p>
              </SubSection>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>
              6. International Transfers
            </SectionTitle>
            <SectionContent>
              <BulletPoint>
                Your data may be processed in countries outside your
                jurisdiction where privacy laws may be different.
              </BulletPoint>
              <BulletPoint>
                We take steps to ensure appropriate safeguards are in place to
                protect your personal information.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>7. Security</SectionTitle>
            <SectionContent>
              <BulletPoint>
                We implement industry-standard administrative, technical and
                physical safeguards to protect personal data.
              </BulletPoint>
              <BulletPoint>
                However, no security measure is unconditional; you use the
                Service at your own risk.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>8. Your Rights</SectionTitle>
            <SectionContent>
              You may have rights to:
              <BulletList>
                <BulletPoint>
                  Access, correct or delete personal data.
                </BulletPoint>
                <BulletPoint>Object to or restrict processing.</BulletPoint>
                <BulletPoint>Data portability.</BulletPoint>
                <BulletPoint>
                  Withdraw consent at any time (processing prior to withdrawal
                  remains lawful).
                </BulletPoint>
              </BulletList>
              You can exercise these rights by contacting us at{" "}
              <EmailLink $theme={theme} href="mailto:support@playbun.com">
                support@playbun.com
              </EmailLink>
              .
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>9. Delete Your Account</SectionTitle>
            <SectionContent>
              You can permanently delete your Playbun.com account at any time:
              <BulletList>
                <BulletPoint>
                  In-app → Settings › Account › Delete Account
                </BulletPoint>
                <BulletPoint>
                  Web →{" "}
                  <EmailLink>https://playbun.com/account/delete</EmailLink>
                </BulletPoint>
              </BulletList>
              Deletion is irreversible. All credits, videos, uploaded images and
              personal data will be completely erased from our systems and we do
              not retain any copies of your content after deletion, except where
              retention is required by law (e.g., fraud-prevention and
              bookkeeping records which do not include your content).
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>10. Children's Privacy</SectionTitle>
            <SectionContent>
              <BulletPoint>
                The Service is not directed to children under 18.
              </BulletPoint>
              <BulletPoint>
                We do not knowingly collect personal data from minors.
              </BulletPoint>
              <BulletPoint>
                If you believe a child has provided us with personal data,
                please contact us to delete it.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>
              11. Changes to This Policy
            </SectionTitle>
            <SectionContent>
              <BulletPoint>
                We may update this Privacy Policy periodically.
              </BulletPoint>
              <BulletPoint>
                We will post the revised version and revise the "Last updated"
                date.
              </BulletPoint>
              <BulletPoint>
                Material changes will be notified by email or prominent notice.
              </BulletPoint>
            </SectionContent>
          </Section>

          <Section>
            <SectionTitle $theme={theme}>12. Contact Us</SectionTitle>
            <SectionContent>
              For questions about privacy, please email{" "}
              <EmailLink $theme={theme} href="mailto:support@playbun.com">
                support@playbun.com
              </EmailLink>
              .
            </SectionContent>
          </Section>

          <Footer $theme={theme}>
            © 2025 Playbun.com All rights reserved.
          </Footer>
        </Content>
      </PrivacyContainer>
    </MainContainer>
  );
};

export default PrivacyPage;

const PrivacyContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  color: ${({ $theme }) => ($theme === "light" ? "#152a47" : "#ffffff")};
  font-family: Figtree;
  font-weight: 400;
  line-height: 1.6;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-family: Figtree;
  font-weight: 600;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #fff, #fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const LastUpdated = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0;
`;

const Content = styled.div``;

const Introduction = styled.p`
  font-size: 16px;
  margin-bottom: 30px;
  padding: 20px;
  background: ${({ $theme }) =>
    $theme === "light"
      ? "rgba(173, 173, 173, 0.05)"
      : "rgba(167, 167, 167, 0.1)"};
  border-left: 4px solid #6eadff;
  border-radius: 8px;
  color: ${({ $theme }) => ($theme === "light" ? "#152a47" : "#ffffff")};
  font-family: Figtree;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-family: Figtree;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  border-bottom: ${({ $theme }) =>
    $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};
  padding-bottom: 8px;
`;

const SubSection = styled.div`
  margin: 20px 0;
  padding-left: 20px;
`;

const SubSectionTitle = styled.h3`
  font-size: 16px;
  font-family: Figtree;
  font-weight: 500;
  color: #fff;
  margin-bottom: 10px;
`;

const SectionContent = styled.div`
  font-size: 14px;
  margin-bottom: 15px;
`;

const BulletList = styled.div`
  margin: 10px 0;
`;

const BulletPoint = styled.div`
  margin: 8px 0;
  padding-left: 20px;
  position: relative;

  &:before {
    content: "•";
    color: #6eadff;
    font-weight: bold;
    position: absolute;
    left: 0;
  }
`;

const Table = styled.div`
  margin: 20px 0;
  border: ${({ $theme }) =>
    $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  background: ${({ $theme }) =>
    $theme === "light"
      ? "rgba(155, 155, 155, 0.05)"
      : "rgba(202, 202, 202, 0.1)"};
  border-bottom: ${({ $theme }) =>
    $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};
`;

const TableHeaderCell = styled.div`
  padding: 15px;
  font-family: Figtree;
  font-weight: 600;
  color: #6eadff;
  font-size: 14px;

  &:first-child {
    border-right: ${({ $theme }) =>
      $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  border-bottom: ${({ $theme }) =>
    $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  padding: 12px 15px;
  font-size: 13px;
  vertical-align: top;
  color: ${({ $theme }) => ($theme === "light" ? "#152a47" : "#ffffff")};

  &:first-child {
    border-right: ${({ $theme }) =>
      $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};
  }
`;

const ImportantNote = styled.div`
  margin: 15px 0;
  padding: 15px;
  background: ${({ $theme }) =>
    $theme === "light"
      ? "rgba(109, 109, 109, 0.05)"
      : "rgba(169, 169, 169, 0.1)"};
  border-left: 4px solid #6eadff;
  border-radius: 6px;
  font-size: 14px;
  color: ${({ $theme }) => ($theme === "light" ? "#152a47" : "#ffffff")};
`;

const HighlightText = styled.div`
  margin: 15px 0;
  padding: 10px 15px;
  background: ${({ $theme }) =>
    $theme === "light" ? "rgba(163, 0, 94, 0.05)" : "rgba(167, 167, 167, 0.1)"};
  border-radius: 6px;
  font-family: Figtree;
  font-weight: 500;
  color: #fff;
  text-align: center;
  font-size: 14px;
`;

const EmailLink = styled.a`
  color: #fff;
  text-decoration: none;

  &:hover {
    color: #fff;
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  margin-top: 50px;
  padding-top: 30px;
  border-top: ${({ $theme }) =>
    $theme === "light" ? "1px solid #e0e0e0" : "1px solid #333"};
  text-align: center;
  font-size: 12px;
  color: ${({ $theme }) =>
    $theme === "light" ? "rgba(26, 26, 46, 0.6)" : "#888"};
`;
